/**
 * Structured Data (JSON-LD) utilities for SEO
 *
 * Generates schema.org markup for better search engine understanding
 */

/**
 * Generate Article schema markup for blog posts
 * Improves search appearance with rich snippets
 *
 * @param {Object} article - Article data
 * @returns {Object} JSON-LD Article schema
 */
export function generateArticleSchema({
  title,
  description,
  author = "Preset",
  datePublished,
  dateModified,
  url,
  imageUrl,
  wordCount,
  category,
  publisher = {
    name: "Preset",
    logo: "https://preset.io/images/content/social_sharing_preset_2023.png",
  },
}) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    author: {
      "@type": "Person",
      name: author,
    },
    datePublished,
    url,
    publisher: {
      "@type": "Organization",
      name: publisher.name,
      logo: {
        "@type": "ImageObject",
        url: publisher.logo,
      },
    },
  }

  // Optional fields
  if (dateModified) {
    schema.dateModified = dateModified
  }

  if (imageUrl) {
    schema.image = {
      "@type": "ImageObject",
      url: imageUrl,
    }
  }

  if (wordCount) {
    schema.wordCount = wordCount
  }

  if (category) {
    schema.articleSection = category
  }

  return schema
}

/**
 * Generate BreadcrumbList schema markup
 * Shows navigation hierarchy in search results
 *
 * @param {Array} breadcrumbs - Array of {name, url} objects
 * @returns {Object} JSON-LD BreadcrumbList schema
 */
export function generateBreadcrumbSchema(breadcrumbs) {
  if (!breadcrumbs || breadcrumbs.length === 0) {
    return null
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  }
}

/**
 * Generate FAQPage schema markup
 * For pages with FAQ sections - helps with featured snippets
 *
 * @param {Array} faqs - Array of {question, answer} objects
 * @returns {Object} JSON-LD FAQPage schema
 */
export function generateFAQSchema(faqs) {
  if (!faqs || faqs.length === 0) {
    return null
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  }
}

/**
 * Generate a unified @graph schema for the homepage
 * Combines Organization, WebSite, WebPage, SoftwareApplication, and FAQPage
 * into a single linked graph for richer search engine understanding.
 *
 * @param {Object} config
 * @returns {Object} JSON-LD @graph schema
 */
export function generateHomePageGraphSchema({
  name = "Preset",
  url = "https://preset.io",
  logo = "https://preset.io/images/content/social_sharing_preset_2023.png",
  description = "Preset is a fully managed cloud platform built on Apache Superset for data exploration, dashboards, and analytics.",
  pageTitle = "Preset | Open Analytics Platform Built on Apache Superset",
  socialLinks = {},
  faqs = [],
}) {
  const sameAs = []
  if (socialLinks.twitter?.url) sameAs.push(socialLinks.twitter.url)
  if (socialLinks.linkedin?.url) sameAs.push(socialLinks.linkedin.url)
  if (socialLinks.github?.url) sameAs.push(socialLinks.github.url)
  if (socialLinks.youtube?.url) sameAs.push(socialLinks.youtube.url)

  const baseUrl = url.replace(/\/$/, "")

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${baseUrl}/#organization`,
        name,
        url: `${baseUrl}/`,
        logo: { "@type": "ImageObject", url: logo },
        description,
        ...(sameAs.length > 0 && { sameAs }),
      },
      {
        "@type": "WebSite",
        "@id": `${baseUrl}/#website`,
        url: `${baseUrl}/`,
        name,
        publisher: { "@id": `${baseUrl}/#organization` },
      },
      {
        "@type": "WebPage",
        "@id": `${baseUrl}/#webpage`,
        url: `${baseUrl}/`,
        name: pageTitle,
        isPartOf: { "@id": `${baseUrl}/#website` },
        about: { "@id": `${baseUrl}/#software` },
        primaryImageOfPage: { "@type": "ImageObject", url: logo },
      },
      {
        "@type": "SoftwareApplication",
        "@id": `${baseUrl}/#software`,
        name,
        url: `${baseUrl}/`,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description:
          "Preset is a fully managed cloud analytics platform built on Apache Superset. It supports SQL-based exploration, dashboard creation, embedded analytics, and connections to 40+ databases.",
        image: logo,
        brand: { "@id": `${baseUrl}/#organization` },
        offers: [
          {
            "@type": "Offer",
            name: "Starter Plan",
            price: "0",
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url: `${baseUrl}/pricing`,
          },
          {
            "@type": "Offer",
            name: "Paid Plans",
            availability: "https://schema.org/InStock",
            url: `${baseUrl}/pricing`,
          },
        ],
        featureList: [
          "Fully managed Apache Superset in the cloud",
          "SQL-based data exploration",
          "Dashboard and chart creation",
          "40+ database connectors",
          "Embedded analytics",
          "Enterprise security and support",
        ],
        creator: { "@id": `${baseUrl}/#organization` },
      },
      ...(faqs.length > 0
        ? [
            {
              "@type": "FAQPage",
              "@id": `${baseUrl}/#faq`,
              mainEntity: faqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: { "@type": "Answer", text: faq.answer },
              })),
            },
          ]
        : []),
    ],
  }
}

/**
 * Generate a unified @graph schema for collection/hub YAML pages.
 * Uses CollectionPage instead of WebPage, supports hasPart links, and
 * references a minimal site-level SoftwareApplication at /#software.
 *
 * @param {Object} config
 * @param {string} config.pageUrl
 * @param {string} config.pageTitle - Short page title (without "| Preset")
 * @param {string} config.pageDescription
 * @param {Array}  config.hasPart - Array of {name, url}
 * @param {string} config.softwareDescription
 * @param {Array}  config.softwareFeatureList
 * @returns {Object} JSON-LD @graph schema
 */
export function generateCollectionPageGraphSchema({
  pageUrl,
  pageTitle,
  pageDescription = "",
  hasPart = [],
  softwareDescription = "Preset is a modern data visualization platform built on Apache Superset.",
  softwareFeatureList = [],
  logo = "https://preset.io/images/content/social_sharing_preset_2023.png",
}) {
  const baseUrl = "https://preset.io"
  const cleanPageUrl = `${pageUrl.replace(/\/$/, "")}/`
  const fullTitle = `${pageTitle} | Preset`

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${baseUrl}/#organization`,
        name: "Preset",
        url: `${baseUrl}/`,
        logo: { "@type": "ImageObject", url: logo },
      },
      {
        "@type": "WebSite",
        "@id": `${baseUrl}/#website`,
        url: `${baseUrl}/`,
        name: "Preset",
        publisher: { "@id": `${baseUrl}/#organization` },
      },
      {
        "@type": "CollectionPage",
        "@id": `${cleanPageUrl}#webpage`,
        url: cleanPageUrl,
        name: fullTitle,
        ...(pageDescription && { description: pageDescription }),
        isPartOf: { "@id": `${baseUrl}/#website` },
        about: { "@id": `${baseUrl}/#software` },
        primaryImageOfPage: { "@type": "ImageObject", url: logo },
        breadcrumb: { "@id": `${cleanPageUrl}#breadcrumb` },
        ...(hasPart.length > 0 && {
          hasPart: hasPart.map((part) => ({
            "@type": "WebPage",
            name: part.name,
            url: part.url,
          })),
        }),
      },
      {
        "@type": "SoftwareApplication",
        "@id": `${baseUrl}/#software`,
        name: "Preset",
        url: `${baseUrl}/`,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        ...(softwareDescription && { description: softwareDescription }),
        image: logo,
        brand: { "@id": `${baseUrl}/#organization` },
        provider: { "@id": `${baseUrl}/#organization` },
        ...(softwareFeatureList.length > 0 && {
          featureList: softwareFeatureList,
        }),
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${cleanPageUrl}#breadcrumb`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: `${baseUrl}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: pageTitle,
            item: cleanPageUrl,
          },
        ],
      },
    ],
  }
}

/**
 * Generate a unified @graph schema for YAML-template pages (LocalPageTemplate).
 * Always produces Organization + WebSite + WebPage + BreadcrumbList.
 * Optionally adds SoftwareApplication (when schemaProduct provided) and FAQPage.
 *
 * @param {Object} config
 * @param {string} config.pageUrl - Canonical page URL
 * @param {string} config.pageTitle - Short page title (without "| Preset")
 * @param {Array}  config.faqs - Array of {question, answer}
 * @param {Object|null} config.schemaProduct - Optional SoftwareApplication data
 * @returns {Object} JSON-LD @graph schema
 */
export function generateLocalPageGraphSchema({
  pageUrl,
  pageTitle,
  logo = "https://preset.io/images/content/social_sharing_preset_2023.png",
  faqs = [],
  schemaProduct = null,
}) {
  const baseUrl = "https://preset.io"
  const cleanPageUrl = `${pageUrl.replace(/\/$/, "")}/`
  const fullTitle = `${pageTitle} | Preset`
  const hasSoftware = !!schemaProduct

  const webPageNode = {
    "@type": "WebPage",
    "@id": `${cleanPageUrl}#webpage`,
    url: cleanPageUrl,
    name: fullTitle,
    isPartOf: { "@id": `${baseUrl}/#website` },
    primaryImageOfPage: { "@type": "ImageObject", url: logo },
    breadcrumb: { "@id": `${cleanPageUrl}#breadcrumb` },
    ...(hasSoftware && { about: { "@id": `${cleanPageUrl}#software` } }),
  }

  const softwareNode = hasSoftware
    ? {
        "@type": "SoftwareApplication",
        "@id": `${cleanPageUrl}#software`,
        name: schemaProduct.name,
        url: cleanPageUrl,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description: schemaProduct.description,
        image: logo,
        screenshot: logo,
        brand: { "@id": `${baseUrl}/#organization` },
        provider: { "@id": `${baseUrl}/#organization` },
        ...(schemaProduct.alternateName && {
          alternateName: schemaProduct.alternateName,
        }),
        ...(schemaProduct.featureList?.length > 0 && {
          featureList: schemaProduct.featureList,
        }),
        offers: {
          "@type": "Offer",
          url: schemaProduct.offerUrl || cleanPageUrl,
          availability: "https://schema.org/InStock",
          category: schemaProduct.offerCategory || "Software",
          ...(schemaProduct.offerDescription && {
            description: schemaProduct.offerDescription,
          }),
        },
      }
    : null

  const faqNode =
    faqs.length > 0
      ? {
          "@type": "FAQPage",
          "@id": `${cleanPageUrl}#faq`,
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: { "@type": "Answer", text: faq.answer },
          })),
        }
      : null

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${baseUrl}/#organization`,
        name: "Preset",
        url: `${baseUrl}/`,
        logo: { "@type": "ImageObject", url: logo },
      },
      {
        "@type": "WebSite",
        "@id": `${baseUrl}/#website`,
        url: `${baseUrl}/`,
        name: "Preset",
        publisher: { "@id": `${baseUrl}/#organization` },
      },
      webPageNode,
      ...(softwareNode ? [softwareNode] : []),
      ...(faqNode ? [faqNode] : []),
      {
        "@type": "BreadcrumbList",
        "@id": `${cleanPageUrl}#breadcrumb`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: `${baseUrl}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: pageTitle,
            item: cleanPageUrl,
          },
        ],
      },
    ],
  }
}

/**
 * Generate a unified @graph schema for the /product page
 * Combines Organization, WebSite, WebPage, SoftwareApplication, and BreadcrumbList
 *
 * @param {Object} config
 * @returns {Object} JSON-LD @graph schema
 */
export function generateProductPageGraphSchema({
  siteUrl = "https://preset.io",
  pageUrl = "https://preset.io/product/",
  logo = "https://preset.io/images/content/social_sharing_preset_2023.png",
  siteName = "Preset",
  pageTitle = "Preset Cloud - Modern Open Source BI Platform | Preset",
  productName = "Preset Cloud",
  description = "Preset Cloud is a fully hosted cloud service for Apache Superset that helps teams explore data, build dashboards, query with SQL, and deliver analytics at scale.",
  featureList = [],
  faqs = [],
  breadcrumbs = [],
  offerDescription = "Starter plan available with free access to Preset Cloud.",
  offers = null,
  applicationCategory = "BusinessApplication",
}) {
  const baseUrl = siteUrl.replace(/\/$/, "")
  const cleanPageUrl = `${pageUrl.replace(/\/$/, "")}/`
  const hasBreadcrumbs = breadcrumbs.length > 0

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${baseUrl}/#organization`,
        name: siteName,
        url: `${baseUrl}/`,
        logo: { "@type": "ImageObject", url: logo },
      },
      {
        "@type": "WebSite",
        "@id": `${baseUrl}/#website`,
        url: `${baseUrl}/`,
        name: siteName,
        publisher: { "@id": `${baseUrl}/#organization` },
      },
      {
        "@type": "WebPage",
        "@id": `${cleanPageUrl}#webpage`,
        url: cleanPageUrl,
        name: pageTitle,
        isPartOf: { "@id": `${baseUrl}/#website` },
        about: { "@id": `${cleanPageUrl}#software` },
        primaryImageOfPage: { "@type": "ImageObject", url: logo },
        ...(hasBreadcrumbs && {
          breadcrumb: { "@id": `${cleanPageUrl}#breadcrumb` },
        }),
      },
      {
        "@type": "SoftwareApplication",
        "@id": `${cleanPageUrl}#software`,
        name: productName,
        url: cleanPageUrl,
        applicationCategory,
        operatingSystem: "Web",
        description,
        image: logo,
        screenshot: logo,
        brand: { "@id": `${baseUrl}/#organization` },
        provider: { "@id": `${baseUrl}/#organization` },
        ...(featureList.length > 0 && { featureList }),
        offers: offers ?? {
          "@type": "Offer",
          url: `${baseUrl}/pricing`,
          price: "0",
          priceCurrency: "USD",
          availability: "https://schema.org/InStock",
          category: "SaaS",
          description: offerDescription,
        },
      },
      ...(faqs.length > 0
        ? [
            {
              "@type": "FAQPage",
              "@id": `${cleanPageUrl}#faq`,
              mainEntity: faqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: { "@type": "Answer", text: faq.answer },
              })),
            },
          ]
        : []),
      ...(hasBreadcrumbs
        ? [
            {
              "@type": "BreadcrumbList",
              "@id": `${cleanPageUrl}#breadcrumb`,
              itemListElement: breadcrumbs.map((crumb, index) => ({
                "@type": "ListItem",
                position: index + 1,
                name: crumb.name,
                item: crumb.url,
              })),
            },
          ]
        : []),
    ],
  }
}

/**
 * Generate SoftwareApplication schema markup
 * For product pages to identify Preset as a software application
 *
 * @param {Object} config - Application configuration
 * @returns {Object} JSON-LD SoftwareApplication schema
 */
export function generateSoftwareApplicationSchema({
  name = "Preset",
  description = "Preset is a modern, enterprise-grade business intelligence platform built on Apache Superset. Create beautiful dashboards, explore data with SQL, and embed analytics in your applications.",
  applicationCategory = "BusinessApplication",
  operatingSystem = "Web Browser",
  url = "https://preset.io",
  screenshot = "https://preset.io/images/content/social_sharing_preset_2023.png",
  offers = null,
  aggregateRating = null,
}) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name,
    description,
    applicationCategory,
    operatingSystem,
    url,
    screenshot,
    provider: {
      "@type": "Organization",
      name: "Preset",
      url: "https://preset.io",
    },
  }

  // Add free tier offer by default
  if (offers === null) {
    schema.offers = {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      description: "Free Starter plan with 5 users, forever free",
    }
  } else if (offers) {
    schema.offers = offers
  }

  // Add aggregate rating if provided
  if (aggregateRating) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ...aggregateRating,
    }
  }

  return schema
}
